import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import {
  RdsAvatarComponent,
  RdsEmptyStateComponent,
  RdsEmptyStateTitleDirective,
  RdsIconComponent,
  RdsProgressSpinnerComponent,
} from '@rds/angular-components';

import {
  RoomHistoryChangeModel,
  RoomHistoryChangesModel,
  RoomHistoryEntryModel,
} from '@bookly/shared';

export interface ChangeValue {
  previous?: string;
  current: string;
}

interface HistoryEntryViewModel {
  revisionNumber: number;
  header: string;
  updatedBy: string;
  updateDate: Date | null;

  active?: ChangeValue;
  automaticApproval?: ChangeValue;
  freezeDays?: ChangeValue;
  generalQuestionsEnabled?: ChangeValue;
  defaultLayoutId?: ChangeValue;
  description?: ChangeValue;
  services?: ChangeValue;
  blockingTimes?: ChangeValue;
  durationRange?: ChangeValue;
}

interface DurationPart {
  days?: number;
  hours?: number;
  minutes?: number;
}

@Component({
  selector: 'bad-room-history',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    RdsAvatarComponent,
    RdsIconComponent,
    RdsEmptyStateComponent,
    RdsEmptyStateTitleDirective,
    RdsProgressSpinnerComponent,
  ],
  templateUrl: './room-history.component.html',
  styleUrl: './room-history.component.scss',
})
export class RoomHistoryComponent {
  readonly entries = input.required<RoomHistoryEntryModel[]>();
  readonly isLoading = input<boolean>(false);

  protected readonly viewModel = computed<HistoryEntryViewModel[]>(() =>
    [...this.entries()]
      .sort((a, b) => b.revisionNumber - a.revisionNumber)
      .map(entry => this.toViewModel(entry))
  );

  /** Maps a raw {@link RoomHistoryEntryModel} to the template-friendly view model. */
  private toViewModel(entry: RoomHistoryEntryModel): HistoryEntryViewModel {
    const changes = entry.changes;

    const viewModel: HistoryEntryViewModel = {
      revisionNumber: entry.revisionNumber,
      header:
        entry.revisionNumber === 0
          ? 'Room created'
          : `Change #${entry.revisionNumber}`,
      updatedBy: entry.updatedBy,
      updateDate: entry.updateDate,

      active: this.toChange(changes['active'], value =>
        this.formatActive(value)
      ),
      automaticApproval: this.toChange(changes['automaticApproval']),
      freezeDays: this.toChange(changes['freezeDays']),
      generalQuestionsEnabled: this.toChange(
        changes['generalQuestionsEnabled']
      ),
      defaultLayoutId: this.toChange(changes['defaultLayoutId']),
      description: this.toChange(changes['description']),
      services: this.toChange(changes['services'], value =>
        this.formatServices(value)
      ),
      blockingTimes: this.blockingTimesChange(changes['blockingTimes']),
      durationRange: this.durationRangeChange(changes['durationRange']),
    };

    return this.fillMissingPrevious(viewModel, entry.revisionNumber === 0);
  }

  /**
   * For actual changes (non-initial entries), renders a missing `previous`
   * value as `'none'` so the template shows `none → current`. The revision 0
   * "Room created" snapshot is left untouched — it is a creation, not a
   * transition.
   */
  private fillMissingPrevious(
    viewModel: HistoryEntryViewModel,
    isInitial: boolean
  ): HistoryEntryViewModel {
    if (isInitial) {
      return viewModel;
    }
    for (const value of Object.values(viewModel)) {
      if (this.isChangeValue(value) && value.previous === undefined) {
        value.previous = 'none';
      }
    }
    return viewModel;
  }

  /** Type guard narrowing a view-model property to a {@link ChangeValue}. */
  private isChangeValue(value: unknown): value is ChangeValue {
    return typeof value === 'object' && value !== null && 'current' in value;
  }

  /** Extracts a leaf change entry (`{current, previous}`) from a raw change value. */
  private leaf(
    raw: RoomHistoryChangeModel | RoomHistoryChangesModel | undefined
  ): RoomHistoryChangeModel | undefined {
    if (!raw || !('current' in raw || 'previous' in raw)) {
      return undefined;
    }
    const change = raw as RoomHistoryChangeModel;
    return change.current != null || change.previous != null
      ? change
      : undefined;
  }

  /**
   * Converts a raw change leaf to a display {@link ChangeValue}. `format`
   * transforms each side's native value into a display string; when omitted, a
   * generic formatter (booleans → `yes`/`no`, empty → `none`) is used.
   */
  private toChange(
    raw: RoomHistoryChangeModel | RoomHistoryChangesModel | undefined,
    format: (value: unknown) => string | undefined = value =>
      this.formatValue(value)
  ): ChangeValue | undefined {
    const change = this.leaf(raw);
    if (!change) {
      return undefined;
    }
    const current = format(change.current);
    const previous =
      change.previous === undefined ? undefined : format(change.previous);
    // When both sides format to empty (e.g. an all-zero duration range), the
    // field carries no information — drop it so it isn't rendered at all.
    if (current === undefined && previous === undefined) {
      return undefined;
    }
    return {
      previous,
      current: current ?? 'none',
    };
  }

  /**
   * Generic native-value formatter:
   * - empty values (`null`, `undefined`, `''`) become `'none'`
   * - booleans become `'yes'` / `'no'`
   */
  private formatValue(value: unknown): string {
    if (value == null || value === '') {
      return 'none';
    }
    if (typeof value === 'boolean') {
      return value ? 'yes' : 'no';
    }
    return String(value);
  }

  /** Formats the room status flag to `active` / `inactive`. */
  private formatActive(value: unknown): string {
    if (value == null || value === '') {
      return 'none';
    }
    if (typeof value === 'boolean') {
      return value ? 'active' : 'inactive';
    }
    return String(value) === 'true' ? 'active' : 'inactive';
  }

  /** Formats a services array into a comma-separated list of service names/types. */
  private formatServices(value: unknown): string {
    if (value == null) {
      return 'none';
    }
    if (!Array.isArray(value)) {
      return String(value);
    }
    if (value.length === 0) {
      return 'none';
    }
    const names = (
      value as Array<{ name?: string; serviceType?: string; type?: string }>
    )
      .map(item => item?.name ?? item?.serviceType ?? item?.type)
      .filter((name): name is string => !!name);
    return names.length > 0 ? names.join(', ') : 'none';
  }

  /**
   * Builds the {@link ChangeValue} for blocking times, supporting both diff
   * shapes the backend emits:
   * - whole-array leaf: `{ current: [{from,to}], previous?: [{from,to}] }`
   * - per-element array diff: `[{ current: {from,to}, previous: {from,to}|null }]`
   */
  private blockingTimesChange(
    raw: RoomHistoryChangeModel | RoomHistoryChangesModel | undefined
  ): ChangeValue | undefined {
    if (!raw) {
      return undefined;
    }

    // Per-element array diff: aggregate each side's slots into one list.
    if (Array.isArray(raw)) {
      const slots = raw as RoomHistoryChangeModel[];
      const currentSlots = slots
        .map(slot => slot?.current)
        .filter(slot => slot != null);
      const previousSlots = slots
        .map(slot => slot?.previous)
        .filter(slot => slot != null);
      const hasPrevious = slots.some(slot => slot && 'previous' in slot);

      return {
        previous: hasPrevious
          ? this.formatBlockingTimes(previousSlots)
          : undefined,
        current: this.formatBlockingTimes(currentSlots),
      };
    }

    // Whole-array leaf shape.
    return this.toChange(raw, value => this.formatBlockingTimes(value));
  }

  /** Formats a blocking-times array into a list of `HH:mm - HH:mm` ranges. */
  private formatBlockingTimes(value: unknown): string {
    if (value == null) {
      return 'none';
    }
    if (!Array.isArray(value)) {
      return String(value);
    }
    if (value.length === 0) {
      return 'none';
    }
    const ranges = (value as Array<{ from?: string; to?: string }>)
      .filter(item => item?.from && item?.to)
      .map(item => `${item.from!.slice(0, 5)} - ${item.to!.slice(0, 5)}`);
    return ranges.length > 0 ? ranges.join(', ') : 'none';
  }

  /**
   * Builds the {@link ChangeValue} for the duration range, supporting both diff
   * shapes the backend emits:
   * - whole-object leaf: `{ current: { min, max }, previous?: { min, max } }`
   * - per-field nested: `{ min: { days: { current, previous }, … }, max: { … } }`
   */
  private durationRangeChange(
    raw: RoomHistoryChangeModel | RoomHistoryChangesModel | undefined
  ): ChangeValue | undefined {
    if (!raw) {
      return undefined;
    }

    // Whole-object leaf shape.
    if ('current' in raw || 'previous' in raw) {
      return this.toChange(raw, value => this.formatDurationRange(value));
    }

    // Per-field nested shape: reconstruct a DurationPart per side from the
    // individual `{ current, previous }` leaves of each min/max field.
    const nested = raw as RoomHistoryChangesModel;
    const currentMin = this.pickDurationSide(nested['min'], 'current');
    const currentMax = this.pickDurationSide(nested['max'], 'current');
    const previousMin = this.pickDurationSide(nested['min'], 'previous');
    const previousMax = this.pickDurationSide(nested['max'], 'previous');

    const hasCurrent = currentMin !== undefined || currentMax !== undefined;
    const hasPrevious = previousMin !== undefined || previousMax !== undefined;
    if (!hasCurrent && !hasPrevious) {
      return undefined;
    }

    return {
      previous: hasPrevious
        ? this.durationLabel({ min: previousMin, max: previousMax })
        : undefined,
      current: hasCurrent
        ? this.durationLabel({ min: currentMin, max: currentMax })
        : 'none',
    };
  }

  /**
   * Extracts one side (`current`/`previous`) of a nested min/max field group
   * into a {@link DurationPart}, or `undefined` when that side carries no value.
   */
  private pickDurationSide(
    fields: unknown,
    side: 'current' | 'previous'
  ): DurationPart | undefined {
    if (!fields || typeof fields !== 'object') {
      return undefined;
    }
    const source = fields as Record<string, RoomHistoryChangeModel>;
    const part: DurationPart = {};
    let hasValue = false;
    for (const key of ['days', 'hours', 'minutes'] as const) {
      const leaf = source[key];
      const value = leaf?.[side];
      if (typeof value === 'number') {
        part[key] = value;
        hasValue = true;
      }
    }
    return hasValue ? part : undefined;
  }

  /**
   * Formats a duration range object into a `Min: Xd Yh Zm / Max: Xd Yh Zm`
   * label, or `undefined` when both min and max are empty (absent or all-zero)
   * so the field is hidden entirely.
   */
  private formatDurationRange(value: unknown): string | undefined {
    if (value == null) {
      return undefined;
    }
    const range = value as { min?: DurationPart; max?: DurationPart };
    if (
      this.isEmptyDurationPart(range.min) &&
      this.isEmptyDurationPart(range.max)
    ) {
      return undefined;
    }
    return this.durationLabel(range);
  }

  /** Renders a `Min: Xd Yh Zm / Max: Xd Yh Zm` label, defaulting missing parts to 0. */
  private durationLabel(range: {
    min?: DurationPart;
    max?: DurationPart;
  }): string {
    const part = (duration?: DurationPart): string =>
      `${duration?.days ?? 0}d ${duration?.hours ?? 0}h ${duration?.minutes ?? 0}m`;
    return `Min: ${part(range.min)} / Max: ${part(range.max)}`;
  }

  /** A duration part is empty when it is absent or all of its fields are zero/unset. */
  private isEmptyDurationPart(duration?: DurationPart): boolean {
    return (
      !duration || (!duration.days && !duration.hours && !duration.minutes)
    );
  }
}
